import type { ElplanErrorAlert, ElplanTranslate } from './elplan.alert.types'
import { isRecord } from './elplan.errorBody.helper'
import { readDetailsString } from './elplan.errorDetails.helper'

const DAY_NOT_CLEARED_CODE = 'ELPLAN_DA_DAY_NOT_CLEARED_AFTER_DELETE'
const DAY_NOT_CLEARED_ALERT_KEY =
  'The bids sent for {{date}} may still be in the recipient’s system. Check, and withdraw them if needed.'

const DAY_NOT_CLEARED_PAST_DEADLINE_CODE = 'ELPLAN_DA_DAY_NOT_CLEARED_PAST_DEADLINE'
const DAY_NOT_CLEARED_PAST_DEADLINE_ALERT_KEY =
  'The bids sent for {{date}} may still be in the recipient’s system. The bid deadline has passed, so they can no longer be withdrawn.'

function resolveDayNotClearedAlert(
  details: Record<string, unknown> | undefined,
  key: string,
  alertKey: string
): ElplanErrorAlert | undefined {
  const date = readDetailsString(details, 'date')
  return date ? { key, values: { date }, alertKey } : undefined
}

// Warning codes are their own `ElplanApiWarningCode` enum on the backend, disjoint from the error
// codes above, so this table never collides with `ELPLAN_DETAILS_ALERT_RESOLVER_BY_CODE`.
const ELPLAN_WARNING_DETAILS_ALERT_RESOLVER_BY_CODE: Record<
  string,
  (details: Record<string, unknown> | undefined) => ElplanErrorAlert | undefined
> = {
  [DAY_NOT_CLEARED_CODE]: (details) =>
    resolveDayNotClearedAlert(details, DAY_NOT_CLEARED_ALERT_KEY, DAY_NOT_CLEARED_CODE),
  [DAY_NOT_CLEARED_PAST_DEADLINE_CODE]: (details) =>
    resolveDayNotClearedAlert(details, DAY_NOT_CLEARED_PAST_DEADLINE_ALERT_KEY, DAY_NOT_CLEARED_PAST_DEADLINE_CODE),
}

const ELPLAN_WARNING_GENERIC_ALERT_KEY = 'The action completed, but a follow-up issue occurred.'

/** Resolves a delete mutation's success-body warnings into their rendered alerts. */
export function resolveElplanWarningAlerts(response: unknown): ElplanErrorAlert[] {
  if (!isRecord(response) || !Array.isArray(response.warnings)) {
    return []
  }

  const alerts: ElplanErrorAlert[] = []
  for (const warning of response.warnings) {
    if (!isRecord(warning) || typeof warning.code !== 'string') {
      continue
    }
    const details = isRecord(warning.details) ? warning.details : undefined
    const detailsAlert = ELPLAN_WARNING_DETAILS_ALERT_RESOLVER_BY_CODE[warning.code]?.(details)
    alerts.push(detailsAlert ?? { key: ELPLAN_WARNING_GENERIC_ALERT_KEY, alertKey: warning.code })
  }
  return alerts
}

/**
 * Composes a delete's resolved warnings into the single translated line `alertStore` shows, or
 * `null` when there is nothing to warn about. The alert copy and this composition live here beside
 * `resolveElplanWarningAlerts`, not in the API-wrapper module.
 */
export function composeElplanWarningAlert(
  response: unknown,
  translate: ElplanTranslate
): { message: string; alertKey?: string } | null {
  const alerts = resolveElplanWarningAlerts(response)
  if (alerts.length === 0) {
    return null
  }
  return {
    message: [...new Set(alerts.map((alert) => translate(alert.key, alert.values)))].join(' '),
    alertKey: alerts[0].alertKey,
  }
}
